import { HEN_BREEDS } from '../data/breeds';
import { ROOSTER_BREEDS } from '../data/roosters';
import { UPGRADES_BY_ID } from '../data/upgrades';
import { INFRA } from '../data/farm';
import { BROODY_PRONE_VALUE_BONUS, FIRST_OF_KIND_BONUS } from '../data/balance';
import type { Effect, HenSpecialId, Stat } from '../data/types';
import { HEN_SPECIALS, ROOSTER_PASSIVES, roosterAura, type ModSink } from './specials';
import type { Flock, GameState, Hen, SimCtx } from './types';

/**
 * The modifier engine. Every multiplier in the game — upgrades, infra, coop,
 * rooster aura & passives, first-of-kind, pair bonus, matriarch traits and
 * breed specials — is folded into per-hen stats here. economy.ts reads ONLY
 * these results, so every bonus reaches real income (digest §4.6).
 *
 * Static modifiers are cached per state and rebuilt when `state.rev`, the
 * wall minute, the local hour or the lifetime-earnings decade changes.
 * Transient buffs (crows, events, combo, rotten) are applied in economy.ts.
 */

export const ALL_STATS: readonly Stat[] = [
  'lay', 'value', 'valueFlat', 'clickValue', 'autoValue', 'gatherRate', 'crowCooldown', 'crowDuration',
  'crowPower', 'autoCrow', 'goldenSpawn', 'silverSpawn', 'goldenWindow', 'silverWindow', 'silverPayout',
  'clutchPower', 'eventDuration', 'eventEffect', 'critterSpawn', 'hatchReward', 'broodSuccess',
  'broodDuration', 'salvageWindow', 'offlineCapHours',
];

export interface Agg {
  mul: Record<Stat, number>;
  add: Record<Stat, number>;
}

export function emptyAgg(): Agg {
  const mul = {} as Record<Stat, number>;
  const add = {} as Record<Stat, number>;
  for (const s of ALL_STATS) {
    mul[s] = 1;
    add[s] = 0;
  }
  return { mul, add };
}

function apply(agg: Agg, stat: Stat, op: 'mul' | 'add', value: number, times = 1): void {
  if (op === 'mul') agg.mul[stat] *= Math.pow(value, times);
  else agg.add[stat] += value * times;
}

function mergeInto(target: Agg, src: Agg): void {
  for (const s of ALL_STATS) {
    target.mul[s] *= src.mul[s];
    target.add[s] += src.add[s];
  }
}

function cloneAgg(src: Agg): Agg {
  return { mul: { ...src.mul }, add: { ...src.add } };
}

/** Per-hen static stats (no transient buffs). */
export interface HenMods {
  lay: number;
  value: number;
  valueFlat: number;
  clickValue: number;
  autoValue: number;
  broodSuccess: number;
  broodDuration: number;
  hatchReward: number;
}

interface ModCache {
  key: string;
  global: Agg;
  flocks: Map<number, Agg>;
  hens: Map<Hen, HenMods>;
}

const caches = new WeakMap<GameState, ModCache>();

function cacheKey(s: GameState, ctx: SimCtx): string {
  const decade = Math.floor(Math.log10(Math.max(1, s.lifetimeEarned)));
  return `${s.rev}|${Math.floor(ctx.wall / 60_000)}|${ctx.localHour}|${decade}`;
}

/** True if this hen is the lowest-index hen of her breed in the flock. */
export function isFirstOfKind(flock: Flock, hen: Hen): boolean {
  for (const b of flock.boxes) {
    if (b.hen?.breed === hen.breed) return b.hen === hen;
  }
  return false;
}

function build(s: GameState, ctx: SimCtx): ModCache {
  const global = emptyAgg();
  const scoped: { eff: Effect; times: number }[] = [];
  const route = (eff: Effect, times = 1) => {
    if (!eff.scope || eff.scope.kind === 'global') apply(global, eff.stat, eff.op, eff.value, times);
    else scoped.push({ eff, times });
  };

  for (const id of s.upgrades) {
    const def = UPGRADES_BY_ID.get(id);
    if (!def || def.category === 'coop') continue;
    for (const eff of def.effects) route(eff);
  }
  for (const def of INFRA) {
    const n = s.infra[def.id] ?? 0;
    if (n > 0) for (const eff of def.perUnit) route(eff, n);
  }

  // Matriarch Clutch Instinct is farm-wide.
  let clutchHens = 0;
  for (const f of s.flocks) for (const b of f.boxes) if (b.hen?.traits.includes('CLUTCH_INSTINCT')) clutchHens++;
  if (clutchHens > 0) {
    const m = 1 + Math.min(0.5, 0.1 * clutchHens);
    apply(global, 'goldenSpawn', 'mul', m);
    apply(global, 'silverSpawn', 'mul', m);
  }

  // Flock-level sources.
  const flocks = new Map<number, Agg>();
  const passiveSeen = new Set<string>();
  for (const f of s.flocks) {
    const fa = emptyAgg();
    for (const id of f.coop) {
      const def = UPGRADES_BY_ID.get(id);
      if (def) for (const eff of def.effects) apply(fa, eff.stat, eff.op, eff.value);
    }
    if (f.rooster) {
      const rb = ROOSTER_BREEDS[f.rooster.breed];
      apply(fa, 'value', 'mul', roosterAura(s, rb.aura, rb.passive));
      const first = !passiveSeen.has(rb.passive);
      passiveSeen.add(rb.passive);
      ROOSTER_PASSIVES[rb.passive].apply?.(
        { state: s, flock: f, first },
        {
          flock: (stat, op, v) => apply(fa, stat, op, v),
          global: (stat, op, v) => apply(global, stat, op, v),
        },
      );
    }
    const mentors = f.boxes.filter((b) => b.hen && !b.hen.broodySince && b.hen.traits.includes('FLOCK_MENTOR')).length;
    if (mentors > 0) apply(fa, 'lay', 'mul', 1 + 0.05 * mentors);
    flocks.set(f.id, fa);
  }

  // Breed specials.
  const farmCount = new Map<HenSpecialId, number>();
  const flockCount = new Map<string, number>();
  for (const f of s.flocks) {
    for (const b of f.boxes) {
      const sp = b.hen ? HEN_BREEDS[b.hen.breed].special : null;
      if (!sp) continue;
      farmCount.set(sp, (farmCount.get(sp) ?? 0) + 1);
      flockCount.set(`${f.id}|${sp}`, (flockCount.get(`${f.id}|${sp}`) ?? 0) + 1);
    }
  }
  const henExtra = new Map<Hen, Agg>();
  const farmSeen = new Set<HenSpecialId>();
  for (const f of s.flocks) {
    const flockSeen = new Set<HenSpecialId>();
    const fa = flocks.get(f.id)!;
    for (const b of f.boxes) {
      const hen = b.hen;
      if (!hen) continue;
      const sp = HEN_BREEDS[hen.breed].special;
      if (!sp) continue;
      const def = HEN_SPECIALS[sp];
      const ctxS = {
        state: s, flock: f, hen, wall: ctx.wall, localHour: ctx.localHour,
        countInFlock: flockCount.get(`${f.id}|${sp}`) ?? 0,
        countInFarm: farmCount.get(sp) ?? 0,
        firstInFlock: !flockSeen.has(sp),
        firstInFarm: !farmSeen.has(sp),
      };
      flockSeen.add(sp);
      farmSeen.add(sp);
      if (!def.apply) continue;
      let extra = henExtra.get(hen);
      const sink: ModSink = {
        hen: (stat, op, v) => {
          if (!extra) { extra = emptyAgg(); henExtra.set(hen, extra); }
          apply(extra, stat, op, v);
        },
        flock: (stat, op, v) => apply(fa, stat, op, v),
        global: (stat, op, v) => apply(global, stat, op, v),
      };
      def.apply(ctxS, sink);
    }
  }

  // Per-hen aggregation.
  const hens = new Map<Hen, HenMods>();
  for (const f of s.flocks) {
    const base = cloneAgg(global);
    mergeInto(base, flocks.get(f.id)!);
    for (const b of f.boxes) {
      const hen = b.hen;
      if (!hen) continue;
      const breed = HEN_BREEDS[hen.breed];
      const a = cloneAgg(base);
      for (const { eff, times } of scoped) {
        const sc = eff.scope!;
        if ((sc.kind === 'breed' && sc.breed === hen.breed) || (sc.kind === 'tier' && sc.tier === breed.tier)) {
          apply(a, eff.stat, eff.op, eff.value, times);
        }
      }
      const extra = henExtra.get(hen);
      if (extra) mergeInto(a, extra);
      if (isFirstOfKind(f, hen)) apply(a, 'value', 'mul', FIRST_OF_KIND_BONUS);
      if (f.rooster && f.rooster.breed === hen.breed && breed.pairBonus !== 1) apply(a, 'value', 'mul', breed.pairBonus);
      if (hen.traits.includes('BROOD_PATCH_MASTERY')) apply(a, 'lay', 'mul', 1.15);
      if (hen.traits.includes('FIERCE_MOTHER')) apply(a, 'value', 'mul', 1.2);
      if (breed.broodyProne && hen.broodySince === null) apply(a, 'value', 'mul', BROODY_PRONE_VALUE_BONUS);
      hens.set(hen, {
        lay: a.mul.lay,
        value: a.mul.value,
        valueFlat: a.add.valueFlat,
        clickValue: a.mul.clickValue,
        autoValue: a.mul.autoValue,
        broodSuccess: a.add.broodSuccess,
        broodDuration: a.mul.broodDuration,
        hatchReward: a.mul.hatchReward,
      });
    }
  }

  return { key: cacheKey(s, ctx), global, flocks, hens };
}

function cache(s: GameState, ctx: SimCtx): ModCache {
  const key = cacheKey(s, ctx);
  let c = caches.get(s);
  if (!c || c.key !== key) {
    c = build(s, ctx);
    caches.set(s, c);
  }
  return c;
}

const NEUTRAL: HenMods = { lay: 1, value: 1, valueFlat: 0, clickValue: 1, autoValue: 1, broodSuccess: 0, broodDuration: 1, hatchReward: 1 };

export function henMods(s: GameState, ctx: SimCtx, hen: Hen): HenMods {
  return cache(s, ctx).hens.get(hen) ?? NEUTRAL;
}

/** Farm-wide multiplier for a stat (1 = none). */
export function globalMul(s: GameState, ctx: SimCtx, stat: Stat): number {
  return cache(s, ctx).global.mul[stat];
}

/** Farm-wide additive value for a stat (0 = none). */
export function globalAdd(s: GameState, ctx: SimCtx, stat: Stat): number {
  return cache(s, ctx).global.add[stat];
}

/** Force a rebuild on next access (tests, after load). */
export function invalidateModifiers(s: GameState): void {
  caches.delete(s);
}
